import {
  CustomCheckbox,
  CustomCollapseCard,
  CustomImage,
  CustomInput,
  successDialog,
  Text,
  useText,
} from '@src/components';
import { Button, Modal, Radio } from 'antd';
import modalClose from '@src/assets/icons/icon-close.svg';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  cardOrdersSave,
  cardOrdersUpdate,
  setCurrentCardOrder,
} from '@src/store/slice/cardOrderSlice';
import { customerLogosGetList } from '@src/store/slice/customerSlice';

const CardParameter = () => {
  const { currentCardOrder } = useSelector((state) => state?.cardOrder);
  const { cardImages } = useSelector((state) => state?.cardImages);
  const dispatch = useDispatch();

  const [companyLogos, setCompanyLogos] = useState([]);
  const [selectedIcon, setSelectedIcon] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [companyInput, setCompanyInput] = useState('');
  const [classicCardImage, setClassicCardImage] = useState(null);

  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  
  useEffect(() => {
    if (cardImages?.length > 0) {
      setClassicCardImage(cardImages?.find((item) => item?.cardType === 0));
    }
  }, [cardImages]);

  useEffect(() => {
    (async () => {
      const action = await dispatch(customerLogosGetList());
      if (customerLogosGetList.fulfilled.match(action)) {
        setCompanyLogos(action?.payload?.data);
      }
    })();
  }, [dispatch]);

  useEffect(() => {
    setCompanyInput(currentCardOrder?.companyName);
  }, [currentCardOrder?.companyName]);

  const parameterInputChange = useCallback(
    async (name, value) => {
      const { id, cardOrderDetails, ...cardOrderBody } = currentCardOrder;
      if (id === 0) {
        if (name === 'printCompanyLogo' && value === true && selectedIcon) {
          await dispatch(cardOrdersSave({ ...cardOrderBody, logoId: selectedIcon, [name]: value }));
        } else if (name === 'printCompanyLogo' && value === false) {
          await dispatch(cardOrdersSave({ ...cardOrderBody, logoId: 0, [name]: value }));
        } else if (name === 'printCompanyLogo' && value === true && selectedIcon === 0) {
          await dispatch(
            cardOrdersSave({ ...cardOrderBody, logoId: 0, printCompanyLogo: false, [name]: value }),
          );
        } else {
          await dispatch(cardOrdersSave({ ...cardOrderBody, [name]: value }));
        }
      } else if (name === 'printCompanyLogo' && value === true && selectedIcon) {
        await dispatch(
          cardOrdersSave({ ...cardOrderBody, logoId: selectedIcon, [name]: value, orderStatus: 0 }),
        );
      } else if (name === 'printCompanyLogo' && value === false) {
        await dispatch(
          cardOrdersSave({ ...cardOrderBody, logoId: 0, [name]: value, orderStatus: 0 }),
        );
      } else if (name === 'printCompanyLogo' && value === true && selectedIcon === 0) {
        await dispatch(
          cardOrdersSave({ ...cardOrderBody, logoId: 0, printCompanyLogo: false, orderStatus: 0 }),
        );
      } else {
        await dispatch(cardOrdersSave({ ...cardOrderBody, [name]: value, orderStatus: 0 }));
      }
    },
    [currentCardOrder, dispatch],
  );

  const handleOrderSave = useCallback(async () => {
    const { id, cardOrderDetails, ...cardOrderBody } = currentCardOrder;
    if (id === 0) {
      const action = await dispatch(
        cardOrdersSave({ ...cardOrderBody, companyName: companyInput }),
      );
      if (cardOrdersSave.fulfilled.match(action)) {
        successDialog({
          title: <Text t="successfullySent" />,
          message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
        });
      }
    } else {
      const action = await dispatch(
        cardOrdersUpdate({
          id,
          ...cardOrderBody,
          companyName: companyInput,
          orderStatus: 0,
        }),
      );
      if (cardOrdersUpdate.fulfilled.match(action)) {
        successDialog({
          title: <Text t="successfullySent" />,
          message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
        });
      }
    }
  }, [currentCardOrder, dispatch, companyInput]);

  const onChange = useCallback((e) => {
    setSelectedIcon(e.target.value);
  }, []);

  const showModal = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const handleOk = useCallback(async () => {
    setIsModalVisible(false);
    dispatch(setCurrentCardOrder({ logoId: selectedIcon, printCompanyLogo: true }));
    const { id, cardOrderDetails, ...cardOrderBody } = currentCardOrder;
    if (id === 0) {
      await dispatch(
        cardOrdersSave({ ...cardOrderBody, logoId: selectedIcon, printCompanyLogo: true }),
      );
    } else {
      await dispatch(
        cardOrdersUpdate({ id, ...cardOrderBody, logoId: selectedIcon, printCompanyLogo: true }),
      );
    }
  }, [dispatch, selectedIcon]);

  const handleCancel = useCallback(async () => {
    dispatch(setCurrentCardOrder({ logoId: 0, printCompanyLogo: false }));
    const { id, cardOrderDetails, ...cardOrderBody } = currentCardOrder;
    if (id === 0) {
      await dispatch(cardOrdersSave({ ...cardOrderBody, logoId: 0, printCompanyLogo: false }));
    } else {
      await dispatch(
        cardOrdersUpdate({ id, ...cardOrderBody, logoId: 0, printCompanyLogo: false }),
      );
    }
    setSelectedIcon(0);
    setIsModalVisible(false);
  }, [dispatch]);

  return (
    <CustomCollapseCard bodyCenter cardTitle={<Text t="cardParameterTitle" />}>
      <div className="card-parameter-container">
        <div className="card-parameter-description">
          <p>
            <Text t="cardParameterDescription" />
          </p>
          <div className="card-parameters-form">
            <div className="parameters-form-item">
              <label className="parameters-form-item-label">
                <div>
                  <Text t="cardParameterPrintName" />:
                </div>
                <CustomCheckbox
                  id="ticketRestaurant-cardOrders-cardInformation-cardParameter-printNameSurname-checkbox"
                  checked={currentCardOrder?.printNameSurname}
                  onChange={(e) => parameterInputChange('printNameSurname', e?.target?.checked)}
                />
              </label>
            </div>
            <div className="parameters-form-item">
              <label className="parameters-form-item-label">
                <div>
                  <Text t="cardParameterPrintCompany" />:
                </div>
                <CustomCheckbox
                  id="ticketRestaurant-cardOrders-cardInformation-cardParameter-printCompanyName-checkbox"
                  checked={currentCardOrder?.printCompanyName}
                  onChange={(e) => parameterInputChange('printCompanyName', e?.target?.checked)}
                />
              </label>
              {currentCardOrder?.printCompanyName && (
                <div className="parameter-input-contain">
                  <CustomInput
                    id="ticketRestaurant-cardOrders-cardInformation-cardParameter-cardParameterSave-input"
                    value={companyInput}
                    onChange={(e) => setCompanyInput(e?.target?.value)}
                  />
                  <span onClick={handleOrderSave}>
                    <Text t="cardParameterSave" />
                  </span>
                </div>
              )}
            </div>

            {companyLogos.length > 0 && (
              <div className="parameters-form-item">
                <label className="parameters-form-item-label">
                  <div>
                    <Text t="cardParameterPrintCompanyLogo" />:
                  </div>
                  <CustomCheckbox
                    id="ticketRestaurant-cardOrders-cardInformation-cardParameter-printCompanyLogo-checkbox"
                    checked={currentCardOrder?.printCompanyLogo}
                    onChange={(e) => {
                      const val = e?.target?.checked;
                      if (val) {
                        setIsModalVisible(true);
                      }

                      parameterInputChange('printCompanyLogo', val);
                    }}
                  />
                </label>

                <div className="parameter-input-contain">
                  <span onClick={showModal}>
                    <Text t="cardParameterChooseLogo" />
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card-template-image">
          {classicCardImage?.contentType && (
            <CustomImage
              className="classic-card-image"
              src={`data:${classicCardImage?.contentType};base64,${classicCardImage?.image}`}
            />
          )}
        </div>
      </div>

      {companyLogos.length > 0 && (
        <Modal
          className="card-parameter-modal"
          width="639px"
          footer={false}
          title={<Text t="cardParameterChooseLogo" />}
          visible={isModalVisible}
          onOk={handleOk}
          onCancel={handleCancel}
          closeIcon={<CustomImage src={modalClose} />}
        >
          <p>
            <Text t="cardParameterChooseLogoDesc" />
          </p>
          <Radio.Group
            id="ticketRestaurant-cardOrders-cardInformation-cardParameter-custom-radioGroup-input-content"
            onChange={onChange}
            value={selectedIcon}
            className="custom-radio-input-content"
          >
            {companyLogos.map((logo) => (
              <Radio
                id="ticketRestaurant-cardOrders-cardInformation-cardParameter-custom-radio-input-content"
                key={logo?.id}
                value={logo?.id}
                className="custom-radio-input"
              >
                <CustomImage src={logo?.logo} />
              </Radio>
            ))}
          </Radio.Group>
          <div className="custom-parameter-modal-footer">
            <span className="custom-btn-cancel" onClick={handleCancel}>
              <Text t="giveUp" />
            </span>
            <Button
              id="cardOrders-cardInformation-cardParameter-custom-btn-primary"
              disabled={!selectedIcon}
              className="custom-btn-primary"
              onClick={handleOk}
            >
              <Text t="okBtn" />
            </Button>
          </div>
        </Modal>
      )}
    </CustomCollapseCard>
  );
};

export default CardParameter;

/*
<div className='parameters-form-item'>
                <label className='parameters-form-item-label'>
                  <div>
                    <Text t='cardParameterPrintCompanyLogo' />:
                  </div>
                  <CustomCheckbox
                    checked={currentCardOrder?.printCompanyLogo}
                    onChange={(e) => parameterInputChange('printCompanyLogo', e?.target?.checked)}
                  />
                </label>

                <div className='parameter-input-contain'>
                  <span onClick={showModal}>
                    <Text t='cardParameterChooseLogo' />
                  </span>
                </div>
              </div>
 */
